import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { buildApiUrl } from "@/config/api";
import { LEGAL_DOCUMENT_VERSION } from "@/shared/legal/documents";
import { API_FETCH_CACHE_MODE, getApiErrorMessage, parseApiResponse, prepareRequestBody } from "@/shared/http/request";
import { AdminSessionContext, type AdminSessionContextValue } from "./context";
import { AdminApiError } from "./errors";
import type { AdminAuthPayload, AdminRequestOptions, AdminUser } from "./types";

const ACCESS_TOKEN_STORAGE_KEY = "asc-admin-access-token";

function decodeBase64Url(value: string) {
  if (typeof atob !== "function") {
    return null;
  }

  const normalizedValue = value.replace(/-/g, "+").replace(/_/g, "/");
  const paddingLength = (4 - (normalizedValue.length % 4 || 4)) % 4;
  const paddedValue = normalizedValue.padEnd(normalizedValue.length + paddingLength, "=");

  return atob(paddedValue);
}

function readAccessTokenExpiration(token: string) {
  const payloadSegment = token.split(".")[1];

  if (!payloadSegment) {
    return null;
  }

  try {
    const payload = JSON.parse(decodeBase64Url(payloadSegment) ?? "{}") as { exp?: number };
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

function isAccessTokenExpiring(token: string, clockSkewSeconds = 120) {
  const expiration = readAccessTokenExpiration(token);

  if (!expiration) {
    return false;
  }

  return Date.now() + clockSkewSeconds * 1000 >= expiration;
}

export function AdminSessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [accessToken, setAccessTokenState] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const refreshPromiseRef = useRef<Promise<boolean> | null>(null);
  const tokenRef = useRef<string | null>(null);

  const setAccessToken = useCallback((token: string | null) => {
    setAccessTokenState(token);
    tokenRef.current = token;

    if (typeof window === "undefined") {
      return;
    }

    if (token) {
      localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, token);
    } else {
      localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
    }
  }, []);

  const clearSession = useCallback(() => {
    setUser(null);
    setAccessToken(null);
  }, [setAccessToken]);

  const applySession = useCallback((payload: AdminAuthPayload) => {
    if (payload.user.role !== "ADMIN") {
      throw new AdminApiError("Ce compte n'a pas les droits administrateur", 403);
    }

    setUser(payload.user);
    setAccessToken(payload.accessToken);
  }, [setAccessToken]);

  const execute = useCallback(async function execute<T>(
    path: string,
    options: AdminRequestOptions = {},
    tokenOverride?: string | null
  ): Promise<T> {
    const { body, headers, skipAuth: _skipAuth, ...init } = options;
    const { requestBody, serializeJsonBody } = prepareRequestBody(body);

    const response = await fetch(buildApiUrl(path), {
      ...init,
      cache: API_FETCH_CACHE_MODE,
      credentials: "include",
      headers: {
        Accept: "application/json",
        ...(serializeJsonBody ? { "Content-Type": "application/json" } : {}),
        ...(headers ?? {}),
        ...(tokenOverride ? { Authorization: `Bearer ${tokenOverride}` } : {})
      },
      body: requestBody
    });

    const payload = await parseApiResponse<T>(response);

    if (!response.ok) {
      throw new AdminApiError(getApiErrorMessage(payload), response.status, payload);
    }

    if (!payload) {
      return undefined as T;
    }

    return payload.data;
  }, []);

  const refreshSession = useCallback(async (): Promise<boolean> => {
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    refreshPromiseRef.current = (async () => {
      try {
        const payload = await execute<AdminAuthPayload>("/auth/refresh", {
          method: "POST",
          body: {}
        });
        applySession(payload);
        return true;
      } catch {
        clearSession();
        return false;
      } finally {
        refreshPromiseRef.current = null;
      }
    })();

    return refreshPromiseRef.current;
  }, [applySession, clearSession, execute]);

  const refreshSessionSilently = useCallback(() => {
    void refreshSession().catch(() => {
      // Keep the current session while the access token is still accepted.
    });
  }, [refreshSession]);

  const request = useCallback(async function request<T>(path: string, options: AdminRequestOptions = {}): Promise<T> {
    const token = options.skipAuth ? null : tokenRef.current;

    try {
      const result = await execute<T>(path, options, token);

      if (token && isAccessTokenExpiring(token)) {
        refreshSessionSilently();
      }

      return result;
    } catch (error) {
      if (!(error instanceof AdminApiError) || options.skipAuth || error.status !== 401) {
        throw error;
      }

      const restored = await refreshSession();
      if (!restored) {
        throw error;
      }

      return execute<T>(path, options, tokenRef.current);
    }
  }, [execute, refreshSession, refreshSessionSilently]);

  const login = useCallback(async (identifier: string, password: string, acceptTerms: boolean) => {
    const payload = await execute<AdminAuthPayload>("/auth/login", {
      method: "POST",
      body: {
        identifier,
        password,
        acceptTerms,
        legalVersion: LEGAL_DOCUMENT_VERSION
      },
      skipAuth: true
    });

    applySession(payload);
  }, [applySession, execute]);

  const changePassword = useCallback(async (input: { currentPassword: string; newPassword: string }) => {
    const payload = await request<AdminAuthPayload>("/auth/change-password", {
      method: "POST",
      body: input
    });

    applySession(payload);
  }, [applySession, request]);

  const updateProfile = useCallback(async (input: { firstName: string; lastName: string; email: string }) => {
    const updatedUser = await request<AdminUser>("/auth/me/profile", {
      method: "PATCH",
      body: input
    });

    setUser(updatedUser);
  }, [request]);

  const logout = useCallback(async () => {
    try {
      await execute("/auth/logout", {
        method: "POST",
        body: {},
        skipAuth: true
      });
    } finally {
      clearSession();
    }
  }, [clearSession, execute]);

  useEffect(() => {
    const restore = async () => {
      const storedToken = typeof window === "undefined" ? null : localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);

      if (storedToken) {
        setAccessToken(storedToken);

        try {
          const profile = await execute<AdminUser>("/auth/me", {}, storedToken);
          if (profile.role === "ADMIN") {
            setUser(profile);
            setIsReady(true);
            return;
          }
        } catch {
          clearSession();
        }
      }

      await refreshSession();
      setIsReady(true);
    };

    void restore();
  }, [clearSession, execute, refreshSession, setAccessToken]);

  const value = useMemo<AdminSessionContextValue>(
    () => ({
      user,
      accessToken,
      isReady,
      isAuthenticated: Boolean(user && accessToken),
      login,
      updateProfile,
      changePassword,
      logout,
      request
    }),
    [accessToken, changePassword, isReady, login, logout, request, updateProfile, user]
  );

  return <AdminSessionContext.Provider value={value}>{children}</AdminSessionContext.Provider>;
}
